/**
 * Phase 4 (Feb 2026) — Connection status card with 5-step semantics.
 *
 * Concept demands: not just "verbunden" but a granular capability-level
 * status. The five states from the concept doc §Verbindungen:
 *   1. configured           — OAuth token present, no calls made yet
 *   2. permission_checked   — capability call succeeded (permissions ok)
 *   3. not_yet_executed     — permission_checked but no real publish yet
 *   4. real_run_proven      — at least one real publish confirmed
 *   5. blocked_or_error     — any error state (revoked, rate-limited,
 *                              permission missing, real-api gate off)
 *
 * The status is derived per-capability from the ad_platform_connections
 * row + last-known publish outcome. The card renders the full ladder
 * so the owner sees exactly WHERE things stand without jargon.
 */
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { CheckCircle2, Circle, AlertTriangle, ShieldCheck, Rocket } from "lucide-react";

const LADDER = [
  { key: "configured",         label: "Verbunden",           icon: Circle },
  { key: "permission_checked", label: "Rechte geprüft",       icon: ShieldCheck },
  { key: "not_yet_executed",   label: "Bereit, noch nicht genutzt", icon: Circle },
  { key: "real_run_proven",    label: "Echt bewiesen",        icon: Rocket },
];

const STATE_META = {
  configured:         { tone: "outline",  text: "Verbunden" },
  permission_checked: { tone: "secondary",text: "Rechte geprüft" },
  not_yet_executed:   { tone: "secondary",text: "Bereit, noch nicht genutzt" },
  real_run_proven:    { tone: "default",  text: "Echt bewiesen" },
  blocked_or_error:   { tone: "destructive", text: "Blockiert / Fehler" },
};

function reachedIndex(state) {
  const order = ["configured", "permission_checked", "not_yet_executed", "real_run_proven"];
  const i = order.indexOf(state);
  return i >= 0 ? i : -1;
}

export default function ConnectionStatusCard({
  platform, connections = [], capability_state, testid,
}) {
  // Derive the effective state.
  const hasConn = connections.length > 0;
  const activeConn = connections.find((c) => c.status === "active");
  const state = !hasConn
    ? "blocked_or_error"
    : capability_state || (activeConn?.last_publish_confirmed_at
        ? "real_run_proven"
        : activeConn?.last_permission_check_ok_at
          ? "not_yet_executed"
          : "configured");
  const meta = STATE_META[state] || STATE_META.blocked_or_error;
  const reached = reachedIndex(state);
  const isError = state === "blocked_or_error";

  return (
    <Card
      data-testid={testid || `connection-card-${platform}`}
      data-state={state}
      className={isError ? "border-destructive" : ""}
    >
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-medium capitalize" data-testid={`${testid || `connection-card-${platform}`}-platform`}>
            {platform}
          </div>
          <Badge variant={meta.tone} data-testid={`${testid || `connection-card-${platform}`}-badge`}>
            {isError && <AlertTriangle className="w-3 h-3 mr-1" />}
            {meta.text}
          </Badge>
        </div>
        <div
          className="grid grid-cols-4 gap-1"
          data-testid={`${testid || `connection-card-${platform}`}-ladder`}
        >
          {LADDER.map((rung, idx) => {
            const Icon = rung.icon;
            const done = idx <= reached && !isError;
            return (
              <div
                key={rung.key}
                data-testid={`${testid || `connection-card-${platform}`}-rung-${rung.key}`}
                data-reached={done ? "true" : "false"}
                className={
                  "flex flex-col items-center gap-1 rounded border p-2 text-[10px] text-center " +
                  (done
                    ? "border-emerald-500 bg-emerald-50/40 dark:bg-emerald-950/20"
                    : "border-muted opacity-60")
                }
              >
                {done ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                ) : (
                  <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                )}
                <span>{rung.label}</span>
              </div>
            );
          })}
        </div>
        {isError && (
          <div
            className="text-xs text-destructive"
            data-testid={`${testid || `connection-card-${platform}`}-error-hint`}
          >
            Verbindung nicht wirksam — bitte in Einstellungen &raquo; Verbindungen prüfen.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export { LADDER, STATE_META };
